import React, { useEffect, useRef, useMemo } from 'react';
import type { Breakpoint } from '../../hooks/useBreakpoint';
import { bpConfig, cisData, orgs, timeCols, sparkColors, genSparkline, genDelta, drawSparkline, drawCompactSparkline } from './data';

interface TrackCardProps {
  mode: 'enterprise' | 'mssp';
  breakpoint: Breakpoint;
}

const DeltaCell: React.FC<{ value: number }> = ({ value }) => {
  const cls = value > 0 ? 'text-anchor-teal bg-[rgba(0,206,209,0.06)]' : value < 0 ? 'text-anchor-red bg-[rgba(239,68,68,0.06)]' : 'text-white/[0.15]';
  const txt = value > 0 ? `+${value}` : value === 0 ? '\u2014' : `${value}`;
  return <div className={`text-center text-[9px] font-medium py-0.5 rounded-[3px] ${cls}`}>{txt}</div>;
};

const TrackCard: React.FC<TrackCardProps> = ({ mode, breakpoint }) => {
  const canvasRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());
  const cfg = bpConfig[breakpoint];

  const { sparkData, orgSparkData, cisDeltas, orgDeltas } = useMemo(() => {
    const sparkData = genSparkline(orgs[0].pct - 12, orgs[0].pct, 4);
    const orgSparkData = orgs.map(o => genSparkline(o.pct - 10 - Math.floor(Math.random() * 6), o.pct, 4));
    const cisDeltas = cisData.map(c => genDelta(c.pct));
    const orgDeltas = orgs.map(o => genDelta(o.pct));
    return { sparkData, orgSparkData, cisDeltas, orgDeltas };
  }, [mode]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      if (mode === 'enterprise') {
        const canvas = canvasRefs.current.get('main');
        if (canvas) {
          const rect = canvas.parentElement!.getBoundingClientRect();
          canvas.width = rect.width * window.devicePixelRatio;
          canvas.height = cfg.sparkHeight * window.devicePixelRatio;
          canvas.style.height = `${cfg.sparkHeight}px`;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
            drawSparkline(canvas, sparkData, '#00CED1');
          }
        }
      } else {
        orgs.forEach((_, idx) => {
          const canvas = canvasRefs.current.get(`org-${idx}`);
          if (canvas) {
            const rect = canvas.parentElement!.getBoundingClientRect();
            const h = cfg.msspSparkHeight;
            canvas.width = rect.width * window.devicePixelRatio;
            canvas.height = h * window.devicePixelRatio;
            canvas.style.height = `${h}px`;
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
              drawCompactSparkline(canvas, orgSparkData[idx], sparkColors[idx] || '#00CED1');
            }
          }
        });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [mode, breakpoint, sparkData, orgSparkData, cfg]);

  const setCanvasRef = (key: string) => (el: HTMLCanvasElement | null) => {
    if (el) canvasRefs.current.set(key, el);
    else canvasRefs.current.delete(key);
  };

  if (mode === 'enterprise') {
    return (
      <div className="glass-card">
        <div className="text-[10px] tracking-[0.12em] uppercase text-anchor-blue mb-1">Step 04 — Track</div>
        <div className="text-xl font-light text-white/90 mb-4">Track Resilience Over Time</div>

        <div className="flex flex-col flex-1 overflow-y-auto gap-2.5" style={{ scrollbarWidth: 'thin' }}>
          <div className="flex items-center gap-4">
            <div className="text-[11px] text-white/60" style={{ minWidth: cfg.deltaLabelCol }}>
              Overall Score
              <small className="block text-[9px] text-white/30 mt-px">{sparkData[0]}% → {sparkData[29]}%</small>
            </div>
            <div className="flex-1" style={{ height: cfg.sparkHeight }}>
              <canvas ref={setCanvasRef('main')} className="w-full h-full block" />
            </div>
          </div>

          <div className="border-t border-white/[0.04] pt-2">
            <div className="grid gap-[4px] px-2 pb-1" style={{ gridTemplateColumns: `${cfg.deltaLabelCol} repeat(10, 1fr)` }}>
              <span className="text-[8px] text-white/25 tracking-[0.04em] uppercase text-left">CIS Module</span>
              {timeCols.map(t => (
                <span key={t} className="text-[8px] text-white/25 tracking-[0.04em] uppercase text-center">{t}</span>
              ))}
            </div>
            {cisData.map((c, ci) => (
              <div
                key={c.num}
                className="grid gap-[4px] items-center py-0.5 px-2"
                style={{ gridTemplateColumns: `${cfg.deltaLabelCol} repeat(10, 1fr)` }}
              >
                <span className="text-[10px] text-white/60 whitespace-nowrap overflow-hidden text-ellipsis" title={c.name}>
                  {c.num}
                </span>
                {cisDeltas[ci].map((d, di) => (
                  <DeltaCell key={di} value={d} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // MSSP mode
  return (
    <div className="glass-card">
      <div className="text-[10px] tracking-[0.12em] uppercase text-anchor-blue mb-1">Step 04 — Track</div>
      <div className="text-xl font-light text-white/90 mb-4">Track Resilience Over Time</div>

      <div className="flex flex-col flex-1 overflow-y-auto gap-[3px]" style={{ scrollbarWidth: 'thin' }}>
        <div className="grid gap-[4px] pb-1 border-b border-white/[0.04] mb-1" style={{ gridTemplateColumns: 'repeat(10, 1fr)', paddingLeft: '90px' }}>
          {timeCols.map(t => (
            <span key={t} className="text-[8px] text-white/25 text-center tracking-[0.04em] uppercase">{t}</span>
          ))}
        </div>

        {orgs.map((o, idx) => (
          <div key={o.name} className="py-1.5 border-b border-white/[0.03] last:border-b-0">
            <div className="flex items-center gap-2.5">
              <div className="min-w-[78px] text-[10px] text-white/60">
                {o.name}
                <small className="block text-[8px] text-white/30 mt-px">{orgSparkData[idx][0]}% → {orgSparkData[idx][29]}%</small>
              </div>
              <div className="flex-1" style={{ height: cfg.msspSparkHeight }}>
                <canvas ref={setCanvasRef(`org-${idx}`)} className="w-full h-full block" />
              </div>
            </div>
            <div className="grid gap-[4px] pt-0.5" style={{ gridTemplateColumns: 'repeat(10, 1fr)', paddingLeft: '90px' }}>
              {orgDeltas[idx].map((d, di) => (
                <DeltaCell key={di} value={d} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TrackCard;
